import { useEffect, useState, Suspense, lazy } from 'react';
import { ThemeProvider } from './contexts/ThemeContext';
import { LoadingProvider, useLoading } from './contexts/LoadingContext';
import { FirestoreDataProvider } from './contexts/FirestoreDataProvider';
import ErrorBoundary from './components/ErrorBoundary';
import FullPageLoader from './components/FullPageLoader/FullPageLoader';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import ScrollToTopButton from './components/ScrollToTopButton/ScrollToTopButton';
import Footer from './components/Footer';
import styled from 'styled-components';
import { getLanguage } from './firebase';
import './App.css';
import './components/Skeletons/skeleton.css';
import SkillsSkeleton from './components/Skeletons/SkillsSkeleton';
import ExperienceSkeleton from './components/Skeletons/ExperienceSkeleton';
import ProjectsSkeleton from './components/Skeletons/ProjectsSkeleton';
import EducationSkeleton from './components/Skeletons/EducationSkeleton';
import TestimonialsSkeleton from './components/Skeletons/TestimonialsSkeleton';
import ContactSkeleton from './components/Skeletons/ContactSkeleton';

const Experience = lazy(() => import('./components/Experience'));

const Skills = lazy(() => import('./components/Skills'));

const Projects = lazy(() => import('./components/Projects'));

const Contact = lazy(() => import('./components/Contact'));

const Education = lazy(() => import('./components/Education'));

const Testimonials = lazy(() => import('./components/Testimonials/index'));

const Body = styled.div`
  background-color: ${({ theme }) => theme.bg};
  width: 100%;
  overflow-x: hidden;
  direction: ${({ $dir }) => $dir || 'ltr'};
`;

const Wrapper = styled.div`
  background:
    linear-gradient(
      38.73deg,
      rgba(5, 150, 105, 0.12) 0%,
      rgba(5, 150, 105, 0) 50%
    ),
    linear-gradient(
      141.27deg,
      rgba(25, 25, 36, 0) 50%,
      rgba(25, 25, 36, 0.08) 100%
    );
  width: 100%;
  clip-path: polygon(0 0, 100% 0, 100% 100%, 30% 98%, 0 100%);
`;

const InitialLoadingRegistrar = () => {
  const { beginExpectedTasks } = useLoading();
  useEffect(() => {
    beginExpectedTasks([
      'heroBio',
      'navbarBio',
      'footerBio',
      'skills',
      'projects',
      'experiences',
      'education',
      'testimonials',
    ]);
  }, [beginExpectedTasks]);
  return null;
};

const SuspenseWrapper = ({ children, fallback, ...ariaProps }) => (
  <div {...ariaProps}>
    <Suspense fallback={fallback}>{children}</Suspense>
  </div>
);

function App() {
  const [currentLang, setCurrentLang] = useState(getLanguage());

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const lang = getLanguage();
    const html = document.documentElement;
    html.setAttribute('lang', lang);
    html.setAttribute('dir', lang === 'ar' ? 'rtl' : 'ltr');
  }, []);

  useEffect(() => {
    const handleLanguageChange = (event) => {
      const newLang = event.detail.language;
      setCurrentLang(newLang);

      requestAnimationFrame(() => {
        const html = document.documentElement;
        html.setAttribute('lang', newLang);
        html.setAttribute('dir', newLang === 'ar' ? 'rtl' : 'ltr');

        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () =>
      window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LoadingProvider>
          <FirestoreDataProvider>
            <FullPageLoader />
            <InitialLoadingRegistrar />
            <Navbar />
            <Body
              $dir={currentLang === 'ar' ? 'rtl' : 'ltr'}
              role="main"
              aria-label="Main content"
            >
              <HeroSection />
              <Wrapper>
                <SuspenseWrapper
                  fallback={<SkillsSkeleton $isRTL={currentLang === 'ar'} />}
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <Skills key={`skills-${currentLang}`} />
                </SuspenseWrapper>
                <SuspenseWrapper
                  fallback={
                    <ExperienceSkeleton $isRTL={currentLang === 'ar'} />
                  }
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <Experience key={`experience-${currentLang}`} />
                </SuspenseWrapper>
              </Wrapper>
              <SuspenseWrapper
                fallback={<ProjectsSkeleton $isRTL={currentLang === 'ar'} />}
                aria-live="polite"
                aria-atomic="true"
              >
                <Projects key={`projects-${currentLang}`} />
              </SuspenseWrapper>
              <Wrapper>
                <SuspenseWrapper
                  fallback={<EducationSkeleton $isRTL={currentLang === 'ar'} />}
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <Education key={`education-${currentLang}`} />
                </SuspenseWrapper>
                <SuspenseWrapper
                  fallback={
                    <TestimonialsSkeleton $isRTL={currentLang === 'ar'} />
                  }
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <Testimonials key={`testimonials-${currentLang}`} />
                </SuspenseWrapper>
                <SuspenseWrapper
                  fallback={<ContactSkeleton $isRTL={currentLang === 'ar'} />}
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <Contact key={`contact-${currentLang}`} />
                </SuspenseWrapper>
              </Wrapper>
              <Footer />
              <ScrollToTopButton />
            </Body>
          </FirestoreDataProvider>
        </LoadingProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
